import { Controller } from '@hotwired/stimulus';
import { TOOL_UNLOCK_TIMING } from '../lib/tool_unlock_timing';

/** Unlock journey: loading toasts + island state while Turbo swaps the member frame. */
export default class extends Controller {
  static targets = ['panel', 'stage', 'toastStack', 'cta'];

  static values = {
    productTitle: String,
    minDurationMs: { type: Number, default: TOOL_UNLOCK_TIMING.minDurationMs },
    messageIntervalMs: { type: Number, default: TOOL_UNLOCK_TIMING.messageIntervalMs },
  };

  connect() {
    this.messageTimer = null;
    this.messageIndex = 0;
    this.unlockStartedAt = null;
    this.onExternalStart = this.onExternalStart.bind(this);
    this.beforeStreamRender = this.beforeStreamRender.bind(this);
    this.element.addEventListener('tool-member-unlock:start', this.onExternalStart);
    document.addEventListener('turbo:before-stream-render', this.beforeStreamRender);
  }

  disconnect() {
    this.clearMessageTimer();
    this.element.removeEventListener('tool-member-unlock:start', this.onExternalStart);
    document.removeEventListener('turbo:before-stream-render', this.beforeStreamRender);
  }

  onExternalStart() {
    this.beginUnlocking();
  }

  startUnlock(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!this.element.contains(form)) return;

    this.beginUnlocking();
  }

  beginUnlocking() {
    this.unlockStartedAt = Date.now();
    this.element.classList.add('tool-member-island--unlocking');
    this.messageIndex = 0;
    this.pushToast(TOOL_UNLOCK_TIMING.messages[0]);
    this.clearMessageTimer();
    this.messageTimer = window.setInterval(() => {
      this.messageIndex = (this.messageIndex + 1) % TOOL_UNLOCK_TIMING.messages.length;
      this.pushToast(TOOL_UNLOCK_TIMING.messages[this.messageIndex]);
    }, this.messageIntervalMsValue);
  }

  /** Call before Turbo.renderStreamMessage when unlock was started via fetch (PWYW $0). */
  renderStreamAfterMinDuration(html) {
    this.scheduleAfterMinDuration(() => {
      window.Turbo?.renderStreamMessage(html);
    });
  }

  scheduleAfterMinDuration(callback) {
    const elapsed = this.unlockStartedAt ? Date.now() - this.unlockStartedAt : 0;
    const wait = Math.max(0, this.minDurationMsValue - elapsed);
    window.setTimeout(callback, wait);
  }

  beforeStreamRender(event) {
    if (!this.element.classList.contains('tool-member-island--unlocking')) return;
    if (!this.unlockStartedAt) return;

    const originalRender = event.detail.render;
    event.detail.render = (streamElement) => {
      this.scheduleAfterMinDuration(() => originalRender(streamElement));
    };
  }

  submitEnd(event) {
    const form = event.detail?.formSubmission?.formElement;
    if (!form || !this.element.contains(form)) return;

    if (!event.detail?.success) {
      this.resetUnlocking();
      this.pushToast('Could not unlock. Try again.', 'error');
    }
  }

  frameLoaded() {
    if (this.element.classList.contains('tool-member-island--unlocked')) {
      this.resetUnlocking();
    }
  }

  resetUnlocking() {
    this.unlockStartedAt = null;
    this.element.classList.remove('tool-member-island--unlocking');
    this.clearMessageTimer();
  }

  clearMessageTimer() {
    if (this.messageTimer) {
      window.clearInterval(this.messageTimer);
      this.messageTimer = null;
    }
  }

  pushToast(message, tone = 'loading') {
    if (!this.hasToastStackTarget) return;

    const toast = document.createElement('p');
    toast.className = `tool-sheet-toast tool-sheet-toast--${tone}`;
    toast.setAttribute('role', tone === 'error' ? 'alert' : 'status');
    toast.innerHTML =
      tone === 'loading'
        ? `<span class="tool-sheet-toast__spinner" aria-hidden="true"></span><span>${message}</span>`
        : `<span class="tool-sheet-toast__icon" aria-hidden="true">${tone === 'error' ? '!' : '✓'}</span><span>${message}</span>`;

    this.toastStackTarget.appendChild(toast);

    const stack = this.toastStackTarget;
    const max = tone === 'loading' ? 2 : 4;
    while (stack.children.length > max) {
      stack.firstElementChild?.remove();
    }
  }
}
